import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IState } from '..';
import { IRichContentEditorState } from '../../../../../viewmodels/AppViewModels';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Label } from '../../../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { TextArea } from '../../../../components-v2/TextArea';
import { SPACE, THEME } from '../../../../components-v2/Theme/theme';
import { CustomDateRange } from '../../../../components/CustomDateRange';
import { ToolbarCustomSurvey } from '../../../../../models/UiUtils';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { RichContentDocumentEditor } from '../../../../components/richContent/RichContentDocumentEditor';
import { CameraIcon } from '../../../../components/svgs/icons/CameraIcon';
import { TimezonePicker } from '../../../../components/TimezonePicker';
import { background } from '../../../../styles/colors';
import { CssSelectors } from '../../../../styles/styles';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { EVENT_TIME_OPTIONS, EventRegistrationFormData, getEventDateTimeText, isValidEventDate } from '../utils';
import { ImagePreviewSmall } from './index';

interface EventDetailsSettingsProps {
	state: {
		enableRequestAttendeesToRSVP: boolean;
		now: Date;
		selectedImage: Api.IFileAttachment | null;
	};
	eventData?: Api.IEventRegistrationSurvey;
	account?: Api.IAccount;
	publishableApiKeys?: Api.IStripeKeys;
	onToggleCheckChanged: () => void;
	onClickMedia: () => void;
	onClickFreeImage: () => void;
	isClone?: boolean;
	setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
}

// Separate component for the Details editor to avoid hooks-in-callback violation
// Resets form dirty state after the editor's initial content change callback,
// since TinyMCE normalizes HTML on init which triggers a false dirty state.
const DetailsEditor: React.FC<{
	value?: IRichContentEditorState;
	onChange: (value: IRichContentEditorState) => void;
}> = ({ value, onChange }) => {
	const { resetField, getValues } = useFormContext<EventRegistrationFormData>();
	const initializedRef = React.useRef(false);

	const handleContentChanged = React.useCallback(
		(content: IRichContentEditorState) => {
			onChange(content);
			// After the editor's first content change (init normalization),
			// reset the form baseline so the normalized HTML becomes the new default.
			if (!initializedRef.current) {
				initializedRef.current = true;
				requestAnimationFrame(() => {
					resetField('details', { defaultValue: getValues('details') });
				});
			}
		},
		[onChange, resetField, getValues]
	);

	return (
		<div className={css(styles.editorContainer)}>
			<RichContentDocumentEditor
				contentState={value}
				onContentStateChanged={handleContentChanged}
				config={{
					autoresizeToFitContent: true,
					minHeight: 250,
					plugins: 'autolink',
					toolbar: ToolbarCustomSurvey,
					toolbarOverflow: 'floating',
				}}
			/>
		</div>
	);
};

export const EventDetailsSettings: React.FC<EventDetailsSettingsProps> = ({
	state,
	onClickMedia,
	onClickFreeImage,
	setState,
}) => {
	const { control, watch, setValue, clearErrors, formState } = useFormContext<EventRegistrationFormData>();
	const formValues = watch();

	// Local computed values
	const hasImages = Boolean(state.selectedImage);

	const endTimeOptions = React.useMemo(() => {
		let options = EVENT_TIME_OPTIONS;
		if (formValues.startDate != null && formValues.startDate === formValues.endDate && formValues.startTime) {
			const startTimeIndex = EVENT_TIME_OPTIONS.findIndex(o => o.dataContext === formValues.startTime);
			options = EVENT_TIME_OPTIONS.slice(startTimeIndex + 1);
		}
		return options;
	}, [formValues.startDate, formValues.endDate, formValues.startTime]);

	const isEventDateValid = React.useMemo(() => {
		return isValidEventDate(formValues);
	}, [formValues]);

	const eventDateTimeText = getEventDateTimeText(formValues);

	// Local handlers
	const onEventTimeChange = React.useCallback(
		(startTime: string) => {
			setValue('startTime', startTime, { shouldDirty: true });
			if (
				formValues.startDate != null &&
				formValues.startDate === formValues.endDate &&
				moment(startTime, 'HH:mm:ss').isSameOrAfter(moment(formValues.endTime, 'HH:mm:ss'))
			) {
				setValue('endTime', undefined, { shouldDirty: true });
			}
		},
		[formValues.startDate, formValues.endDate, formValues.endTime, setValue]
	);

	const onAllDayChange = (isAllDay: boolean) => {
		setValue('isAllDay', isAllDay, { shouldDirty: true });
		clearErrors(['startTime', 'endTime']);
	};

	const onEventTimeEndChange = React.useCallback(
		(endTime: string) => {
			setValue('endTime', endTime, { shouldDirty: true });
		},
		[setValue]
	);

	const selectedStartTime = formValues.startTime;
	const selectedEndTime = formValues.endTime;

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Event Details</h3>

			<FormFieldWrapper
				name='location'
				control={control}
				title='Event location'
				description='Add Location for this event'
				textWrapperStyles={[styles.textWrapper]}
				containerStyles={[styles.inputContainer]}
				controllerWrapperStyles={[styles.controllerWrapper]}
			>
				{({ value, onChange, onBlur, ref }) => (
					<TextArea id='eventLocation' value={value} onChange={onChange} onBlur={onBlur} ref={ref} />
				)}
			</FormFieldWrapper>

			<FormFieldWrapper
				name='details'
				control={control}
				title='Details'
				description='This will show up as a description for your event'
				textWrapperStyles={[styles.textWrapper]}
				containerStyles={[styles.inputContainer]}
				controllerWrapperStyles={[styles.controllerWrapper]}
			>
				{({ value, onChange }) => <DetailsEditor value={value} onChange={onChange} />}
			</FormFieldWrapper>

			<div className={css(styles.inputContainer, styleSheet.formControlWrap)}>
				<div className={css(styles.textWrapper)}>
					<h4
						style={{
							fontSize: THEME.typography.fontSizes.md,
							margin: 0,
							marginBottom: THEME.spacing.xs,
							color: THEME.colors.text.main,
						}}
					>
						Event Date & Time
					</h4>
				</div>
				<div className={css(styles.controllerWrapper)}>
					<Popover>
						<PopoverTrigger asChild>
							<div className={css(styleSheet.eventDateInput)}>
								<button type='button' className={`endDate-date-field-button ${css(styleSheet.eventDateButton)}`}>
									<span className={css(styleSheet.selectedDate)}>{eventDateTimeText}</span>
								</button>
							</div>
						</PopoverTrigger>
						<PopoverContent side='top'>
							<CustomDateRange
								minDate={state.now}
								from={formValues.startDate ? new Date(formValues.startDate) : undefined}
								to={formValues.endDate ? new Date(formValues.endDate) : undefined}
								resetDates={() => {
									setValue('startDate', undefined as any, { shouldDirty: true });
									setValue('endDate', undefined as any, { shouldDirty: true });
									setValue('startTime', undefined as any, { shouldDirty: true });
									setValue('endTime', undefined as any, { shouldDirty: true });
								}}
								onChange={(from: Date, to?: Date) => {
									const fromDateStr = moment(from).format('YYYY/MM/DD');
									setValue('startDate', fromDateStr, { shouldDirty: true });
									setValue('endDate', to ? moment(to).format('YYYY/MM/DD') : fromDateStr, { shouldDirty: true });
									setValue('startTime', undefined as any, { shouldDirty: true });
									setValue('endTime', undefined as any, { shouldDirty: true });
								}}
								styles={[styleSheet.dateRangePickerWrapper]}
								datePickerStyles={[styleSheet.dateRangePicker]}
							/>
							<Label htmlFor='all-day-event' styles={[styles.allDayLabel]}>
								<Checkbox
									id='all-day-event'
									checked={formValues.isAllDay}
									onCheckedChange={checked => onAllDayChange(checked === true)}
								/>
								All day
							</Label>
							<div className={css(styleSheet.selectTimeWrap)}>
								{formValues.isAllDay ? null : (
									<>
										<div>
											<Label htmlFor='start-time-input' styles={[styleSheet.selectDateLabel]}>
												Start Time
											</Label>
											<Select
												items={EVENT_TIME_OPTIONS.map(o => ({ label: o.text, value: o.dataContext }))}
												value={selectedStartTime || undefined}
												onValueChange={onEventTimeChange}
											>
												<SelectTrigger
													id='start-time-input'
													styles={[styleSheet.selectDate]}
													disabled={!formValues.startDate}
												>
													{selectedStartTime ? <SelectValue /> : <span>Select time</span>}
												</SelectTrigger>
												<SelectContent>
													{EVENT_TIME_OPTIONS.map(option => (
														<SelectItem key={option.id} value={option.dataContext}>
															{option.text}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div>
											<Label htmlFor='end-time-input' styles={[styleSheet.selectDateLabel]}>
												End Time
											</Label>
											<Select
												items={endTimeOptions.map(o => ({ label: o.text, value: o.dataContext }))}
												value={selectedEndTime || undefined}
												onValueChange={onEventTimeEndChange}
											>
												<SelectTrigger
													id='end-time-input'
													styles={[styleSheet.selectDate]}
													disabled={!formValues.endDate}
												>
													{selectedEndTime ? <SelectValue /> : <span>Select time</span>}
												</SelectTrigger>
												<SelectContent>
													{endTimeOptions.map(option => (
														<SelectItem key={option.id} value={option.dataContext}>
															{option.text}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</>
								)}
								<Controller
									name='timezone'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => {
										return (
											<div>
												<Label htmlFor='timezone-picker-input' styles={[styleSheet.selectDateLabel]}>
													Timezone
												</Label>
												<TimezonePicker
													initialTimezoneValue={formValues.timezone}
													onTimezoneSelected={(tz: { name: string }) => fieldProps.onChange(tz.name)}
													inputStyles={[styleSheet.timezonePicker]}
												/>
											</div>
										);
									}}
								/>
							</div>
						</PopoverContent>
					</Popover>
					{formState.isSubmitted && !isEventDateValid ? (
						<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>Event date and time is required</p>
					) : null}
				</div>
			</div>

			<div className={css(styleSheet.noMargin)}>
				<label htmlFor='event-image-input' className={css(styleSheet.eventLabel)}>
					Upload an event image (optional)
				</label>
				{!hasImages ? (
					<div className={css(styleSheet.addMediaContainer, styleSheet.contentContainers)}>
						<div>
							<button
								type='button'
								className={css(styleSheet.iconButtons)}
								disabled={hasImages}
								onClick={onClickFreeImage}
								title={hasImages ? 'Only one image is supported at this time' : undefined}
							>
								<CameraIcon />
								<div className={css(styleSheet.freeImage)}> Free Images</div>
							</button>
							<div className={css(styleSheet.sourceMention)}>Royalty free image library</div>
						</div>
						<div>
							<button
								type='button'
								className={css(styleSheet.ownImage)}
								disabled={hasImages}
								onClick={onClickMedia}
								title={hasImages ? 'Only one image is supported at this time' : undefined}
							>
								+ Upload Media
							</button>
							<div className={css(styleSheet.sourceMention)}>Choose file from your computer</div>
						</div>
					</div>
				) : (
					<div>
						{state?.selectedImage ? (
							<ImagePreviewSmall
								name={state?.selectedImage?.fileName}
								onRemoveClick={() => {
									setState(prevState => ({
										...prevState,
										selectedImage: null,
									}));
								}}
								url={state?.selectedImage?.url}
							/>
						) : null}
					</div>
				)}
			</div>
		</section>
	);
};

const { spacing } = THEME;

const styles = StyleSheet.create({
	allDayLabel: {
		marginBottom: SPACE[16],
	},
	textWrapper: {
		minWidth: '100%',
		marginBottom: spacing.sm,
	},
	inputContainer: {
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
	},
	controllerWrapper: {
		width: '100%',
		maxWidth: 700,
		display: 'flex',
		flexDirection: 'column',
	},
	editorContainer: {
		...CssSelectors.allDescendants(
			{
				padding: spacing.md,
				background,
				borderRadius: THEME.common.borderRadius,
				border: `${THEME.common.border} !important` as const,
			},
			'.tox-edit-area'
		),
		...CssSelectors.allDescendants(
			{
				background: 'none',
			},
			'.tox-toolbar-overlord .tox-toolbar'
		),
	},
});
